import { useEffect } from "react";
import { ReactivationRequestForm } from "./ReactivationRequestForm";

interface PausedMapSplashProps {
  orgSlug: string;
  mapSlug: string;
  orgName: string;
  orgLogoUrl: string | null;
  pausedAt: string;
  isEmbed: boolean;
}

const REACTIVATION_EMAIL = "connor@wondermaps.co";

/**
 * Co-branded splash shown when a map is paused (plan 122).
 *
 * Defense-in-depth on crawlers: emits `<meta name="robots" content="noindex,
 * nofollow">` from React. The API also sets X-Robots-Tag on the JSON, so both
 * layers cover us if CF Pages strips one.
 *
 * Embed mode renders a stripped variant — pitching Wondermaps inside the
 * venue's own iframe is awkward, so we drop the form + co-branding.
 */
export function PausedMapSplash({
  orgSlug,
  mapSlug,
  orgName,
  orgLogoUrl,
  isEmbed,
}: PausedMapSplashProps) {
  // Defense-in-depth: inject <meta name="robots"> unconditionally.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 px-6 py-10">
      <div className="flex w-full max-w-md flex-col items-center gap-6 text-center">
        {isEmbed ? (
          <img
            src="/wondermaps-logo-primary.png"
            alt="Wondermaps"
            className="h-10 object-contain"
          />
        ) : (
          <CoBrandedHeader orgName={orgName} orgLogoUrl={orgLogoUrl} />
        )}

        <h1 className="text-2xl font-semibold text-slate-900">
          This map is taking a break
        </h1>

        {!isEmbed && (
          <>
            <p className="text-sm text-slate-600">
              We&apos;ll be back. In the meantime, reach out if you&apos;d like
              to bring this experience back online.
            </p>
            <div className="w-full text-left">
              <ReactivationRequestForm orgSlug={orgSlug} mapSlug={mapSlug} />
            </div>
            <p className="text-xs text-slate-500">
              Or email us at{" "}
              <a
                href={`mailto:${REACTIVATION_EMAIL}`}
                className="underline hover:text-slate-700"
              >
                {REACTIVATION_EMAIL}
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function CoBrandedHeader({
  orgName,
  orgLogoUrl,
}: {
  orgName: string;
  orgLogoUrl: string | null;
}) {
  return (
    <div className="flex items-center gap-4">
      <img
        src="/wondermaps-logo-primary.png"
        alt="Wondermaps"
        className="h-10 object-contain"
      />
      <span aria-hidden className="h-8 w-px bg-slate-300" />
      <OrgMark orgName={orgName} orgLogoUrl={orgLogoUrl} />
    </div>
  );
}

function OrgMark({
  orgName,
  orgLogoUrl,
}: {
  orgName: string;
  orgLogoUrl: string | null;
}) {
  if (!orgLogoUrl) {
    return (
      <span className="text-lg font-semibold text-slate-900">{orgName}</span>
    );
  }
  return (
    <img
      src={orgLogoUrl}
      alt={orgName}
      className="h-10 object-contain"
      onError={(e) => {
        // Graceful fallback if logo URL is broken — swap to text node.
        const span = document.createElement("span");
        span.className = "text-lg font-semibold text-slate-900";
        span.textContent = orgName;
        e.currentTarget.replaceWith(span);
      }}
    />
  );
}
